"use client"

import clsx from "clsx"
import { format } from "date-fns"
import { useEffect, useRef, useState } from "react"
import { enUS, ja, ko } from "react-day-picker/locale"
import { TButton } from "../button/TButton"
import { TTextButton } from "../button/TTextButton"
import { TDivider } from "../display/TDivider"
import { TIcon } from "../display/TIcon"
import { Calendar } from "../shadcn/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/popover"
import { ScrollArea } from "../shadcn/scroll-area"

/**
 * @description 날짜+시간 선택. 캘린더와 시간 선택기가 결합
 * @example
 * ```tsx
 * <TDateTimePicker
 *   value={selectedDateTime}
 *   ampm={false}
 *   minutesStep={15}
 *   onChange={setSelectedDateTime}
 * />
 * ```
 */
interface TDateTimePickerProps {
	/** 선택된 날짜+시간 */
	value?: Date
	/**
	 * 입력 필드 크기
	 * @default "medium"
	 * @ai-style "small" - 높이 32px
	 * @ai-style "medium" - 높이 40px
	 */
	size?: "small" | "medium"
	/** 비활성화 여부 */
	disabled?: boolean
	/** 에러 상태 표시 */
	error?: boolean
	/** 과거 날짜 선택 불가 */
	disablePast?: boolean
	/** 미래 날짜 선택 불가 */
	disableFuture?: boolean
	/**
	 * 캘린더 로케일
	 * @default "en"
	 */
	currentLang?: "ko" | "ja" | "en"
	/** 추가 CSS 클래스 */
	className?: string
	/** 날짜 미선택 시 표시할 텍스트 */
	placeholder?: string
	/** 팝업 가로 정렬 */
	align?: "start" | "center" | "end"
	/** 팝업 표시 방향 */
	side?: "top" | "right" | "bottom" | "left"
	/** 팝업 가로 정렬 오프셋 (px) */
	alignOffset?: number
	/** 팝업 방향 오프셋 (px) */
	sideOffset?: number
	/**
	 * 12시간제(true) / 24시간제(false)
	 * @default true
	 */
	ampm?: boolean
	/**
	 * 분 단위 간격. 예: 5 = 0, 5, 10, 15...
	 * @default 5
	 */
	minutesStep?: number
	/** 날짜+시간 선택 시 콜백 */
	onChange?: (date: Date) => void
}

const adjustMinutesToStep = (date: Date, step: number): Date => {
	if (step <= 1) {
		return date
	}
	const newDate = new Date(date)
	const minutes = newDate.getMinutes()
	const remainder = minutes % step
	if (remainder !== 0) {
		const adjustedMinutes = Math.ceil(minutes / step) * step
		if (adjustedMinutes >= 60) {
			newDate.setMinutes(0, 0, 0)
			newDate.setHours(newDate.getHours() + 1)
		} else {
			newDate.setMinutes(adjustedMinutes, 0, 0)
		}
	}
	return newDate
}

export const TDateTimePicker = (props: TDateTimePickerProps) => {
	const {
		value,
		size = "medium",
		disabled,
		error,
		disablePast,
		disableFuture,
		currentLang = "en",
		className,
		placeholder = "날짜 및 시간 선택",
		align = "start",
		side = "bottom",
		alignOffset = 0,
		sideOffset = 4,
		ampm = true,
		minutesStep = 5,
		onChange
	} = props

	const [open, setOpen] = useState<boolean>(false)
	const [selectedDate, setSelectedDate] = useState<Date>(() => adjustMinutesToStep(value || new Date(), minutesStep))

	const hourScrollRef = useRef<HTMLDivElement | null>(null)
	const minuteScrollRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (value) {
			const adjustedValue = adjustMinutesToStep(value, minutesStep)
			if (adjustedValue.getTime() !== selectedDate.getTime()) {
				setSelectedDate(adjustedValue)
			}
		} else {
			setSelectedDate(adjustMinutesToStep(new Date(), minutesStep))
		}
	}, [value, minutesStep])

	useEffect(() => {
		if (open) {
			const timerId = setTimeout(() => {
				if (!hourScrollRef.current || !minuteScrollRef.current) return

				const currentHour = ampm ? selectedDate.getHours() % 12 || 12 : selectedDate.getHours()
				const hourElement = hourScrollRef.current.querySelector(`[data-hour="${currentHour}"]`)
				hourElement?.scrollIntoView({ block: "center", behavior: "auto" })

				const currentMinute = selectedDate.getMinutes()
				const minuteElement = minuteScrollRef.current.querySelector(`[data-minute="${currentMinute}"]`)
				minuteElement?.scrollIntoView({ block: "center", behavior: "auto" })
			}, 0)

			return () => clearTimeout(timerId)
		}
	}, [open, selectedDate, ampm])

	const localeMap = {
		ko: ko,
		ja: ja,
		en: enUS
	}
	const selectedLocale = localeMap[currentLang]

	const handleDateChange = (date: Date | undefined) => {
		if (date) {
			const newDate = new Date(selectedDate)
			newDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate())
			const adjustedDate = adjustMinutesToStep(newDate, minutesStep)
			setSelectedDate(adjustedDate)
		}
	}

	const handleTimeChange = (hours: number, minutes: number) => {
		const newDate = new Date(selectedDate)
		newDate.setHours(hours, minutes, 0, 0)
		setSelectedDate(newDate)
	}

	const handleReset = () => {
		setSelectedDate(adjustMinutesToStep(value || new Date(), minutesStep))
	}

	const handleApply = () => {
		onChange?.(selectedDate)
		setOpen(false)
	}

	const handleCancel = () => {
		setSelectedDate(adjustMinutesToStep(value || new Date(), minutesStep))
		setOpen(false)
	}

	const renderTimeList = () => {
		const hours = ampm ? Array.from({ length: 12 }, (_, i) => i + 1) : Array.from({ length: 24 }, (_, i) => i)
		const minutes = Array.from({ length: Math.floor(60 / minutesStep) }, (_, i) => i * minutesStep)

		return (
			<div className="flex space-x-1">
				{ampm && (
					<div className="flex flex-col space-y-0.5">
						<div className="flex w-10 [&_button]:w-full">
							<TButton
								size="xsmall"
								color={selectedDate.getHours() < 12 ? "primary" : "tertiary"}
								onClick={() => {
									const currentHourIn24 = selectedDate.getHours()
									const newHour = currentHourIn24 >= 12 ? currentHourIn24 - 12 : currentHourIn24
									handleTimeChange(newHour, selectedDate.getMinutes())
								}}
							>
								AM
							</TButton>
						</div>
						<div className="flex w-10 [&_button]:w-full">
							<TButton
								size="xsmall"
								color={selectedDate.getHours() >= 12 ? "primary" : "tertiary"}
								onClick={() => {
									const currentHourIn24 = selectedDate.getHours()
									const newHour = currentHourIn24 < 12 ? currentHourIn24 + 12 : currentHourIn24
									handleTimeChange(newHour === 24 ? 12 : newHour, selectedDate.getMinutes())
								}}
							>
								PM
							</TButton>
						</div>
					</div>
				)}
				<ScrollArea className="h-[340px]" ref={hourScrollRef}>
					<div className={clsx("flex flex-col space-y-0.5", hours.length * 28 > 340 ? "pr-2" : "")}>
						{hours.map((hour) => (
							<div key={`hour-${hour}`} className="flex w-10 [&_button]:w-full" data-hour={hour}>
								<TButton
									size="xsmall"
									color={hour === (ampm ? selectedDate.getHours() % 12 || 12 : selectedDate.getHours()) ? "primary" : "tertiary"}
									onClick={() => {
										const currentMinutes = selectedDate.getMinutes()
										let newHourIn24 = hour
										if (ampm) {
											const isCurrentlyPM = selectedDate.getHours() >= 12
											if (isCurrentlyPM && hour !== 12) {
												newHourIn24 = hour + 12
											} else if (!isCurrentlyPM && hour === 12) {
												newHourIn24 = 0
											} else if (isCurrentlyPM && hour === 12) {
												newHourIn24 = 12
											}
										}
										handleTimeChange(newHourIn24, currentMinutes)
									}}
								>
									{hour.toString().padStart(2, "0")}
								</TButton>
							</div>
						))}
					</div>
				</ScrollArea>
				<ScrollArea className="h-[340px]" ref={minuteScrollRef}>
					<div className={clsx("flex flex-col space-y-0.5", minutes.length * 28 > 340 ? "pr-2" : "")}>
						{minutes.map((minute) => (
							<div key={`minute-${minute}`} className="flex w-10 [&_button]:w-full" data-minute={minute}>
								<TButton
									size="xsmall"
									color={minute === selectedDate.getMinutes() ? "primary" : "tertiary"}
									onClick={() => handleTimeChange(selectedDate.getHours(), minute)}
								>
									{minute.toString().padStart(2, "0")}
								</TButton>
							</div>
						))}
					</div>
				</ScrollArea>
			</div>
		)
	}

	const renderFooter = () => (
		<footer className="flex justify-between items-center">
			<TTextButton size="small" color="secondary" onClick={handleReset}>
				Reset
			</TTextButton>
			<div className="flex items-center space-x-2">
				<TButton size="small" color="line" onClick={handleCancel}>
					Cancel
				</TButton>
				<TButton size="small" color="primary" onClick={handleApply}>
					Apply
				</TButton>
			</div>
		</footer>
	)

	return (
		<Popover
			onOpenChange={(isOpen) => {
				setOpen(isOpen)
				if (!isOpen) {
					handleCancel()
				}
			}}
			open={open}
		>
			<PopoverTrigger asChild>
				<div className={clsx("TDate time-picker", size, className)} data-open={open} data-disabled={disabled} data-error={error}>
					<label className={clsx("flex-1 pl-3 text-base font-medium select-none cursor-pointer", !value && "text-text-placeholder")}>
						{value ? format(selectedDate, ampm ? "yyyy.MM.dd a hh:mm" : "yyyy.MM.dd HH:mm", { locale: selectedLocale }) : placeholder}
					</label>
					<div className="suffix ml-2 mr-3">
						<TIcon size={16} name="calendar" />
					</div>
				</div>
			</PopoverTrigger>
			<PopoverContent className="space-y-4" align={align} side={side} alignOffset={alignOffset} sideOffset={sideOffset}>
				<div className="flex space-x-4">
					<Calendar
						mode="single"
						locale={selectedLocale}
						defaultMonth={selectedDate}
						selected={selectedDate}
						onSelect={handleDateChange}
						disabled={(date: Date) => {
							if (disablePast && date < new Date(new Date().toDateString())) {
								return true
							}
							if (disableFuture && date > new Date(new Date().toDateString())) {
								return true
							}
							return false
						}}
						formatters={{
							formatWeekdayName: (date) => format(date, "EEE", { locale: selectedLocale }),
							formatCaption: (date) =>
								currentLang === "ko"
									? format(date, "yyyy년 M월", { locale: ko })
									: currentLang === "ja"
										? format(date, "yyyy年M月", { locale: ja })
										: format(date, "LLLL yyyy", { locale: enUS })
						}}
					/>
					<TDivider orientation="vertical" className="h-auto! self-stretch" />
					{renderTimeList()}
				</div>
				<TDivider />
				{renderFooter()}
			</PopoverContent>
		</Popover>
	)
}

TDateTimePicker.displayName = "TDateTimePicker"
